import { errorHandler } from "@/services/toastHandler";

const initState = () => ({
  loading: false,
  error: "",
  totalPage: 0,
  data: {
    users: 0,
    buyInBalance: 0,
    payout: 0,
    depositBalance: 0,
    createdCompetitions: 0,
  },
  loadingSyncTag: false,
  userTags: [],

  tables: { top10Winners: [], top10Losers: [] },
  charts: null,
});

export default {
  namespaced: true,
  state: initState(),
  mutations: {
    SET_DATA: (s, data) => (s.data = { ...data }),
    SET_USER_TAGS: (s, tags) => (s.userTags = tags),
    LOADING: (s) => (s.loading = true),
    LOADING_SYNC_TAG: (s, data) => (s.loadingSyncTag = data),
    SET_ERROR: (s, error) => (s.error = error),
    LOADED: (s) => (s.loading = false),
    RESET: (s) => {
      Object.assign(s, initState());
    },
    SET_DATA_DASHBOARD: (s, data) => {
      s.tables = data.tables;
      s.charts = data.charts;
    },
  },

  actions: {
    async load({ commit }, params) {
      commit("LOADING");
      try {
        const res = await this.$api.get(
          `/portal/dashboard?dateFilter[startDate]=${params.startDate}&dateFilter[endDate]=${params.endDate}`
        );
        return res.data.data;
      } catch (e) {
        errorHandler(e);
      } finally {
        commit("LOADED");
      }
    },
    async getUserTag({ commit }, userId) {
      try {
        const { data: res } = await this.$api.get(
          `/v1/portal/users/${userId}/tags`
        );
        commit("SET_USER_TAGS", res.data);
      } catch (e) {
        errorHandler(e);
      }
    },
    async syncTagForUser({ commit }, { userId, tags }) {
      try {
        commit("LOADING_SYNC_TAG", true);
        const tagIds = tags.map((tag) => +tag.tagId);
        const { data: res } = await this.$api.post(
          `/v1/portal/users/${userId}/tags`,
          {
            tagIds,
          }
        );
        if (res.data) commit("SET_USER_TAGS", tags);
      } catch (e) {
        errorHandler(e);
      } finally {
        commit("LOADING_SYNC_TAG", false);
      }
    },
    reset({ commit }) {
      commit("RESET");
    },
    async getDataDashboard({ commit }, params) {
      commit("LOADING");
      try {
        const { dateFrom, dateTo, timePeriod, bdmTag } = params;
        const queryParams = {};
        if (dateFrom && dateTo) {
          queryParams.dateFrom = dateFrom;
          queryParams.dateTo = dateTo;
        } else if (timePeriod) {
          queryParams.timePeriod = timePeriod;
        }
        if (bdmTag) queryParams.bdmTag = bdmTag;

        const { data: res } = await this.$api.get(
          `/v1/portal/reports/vip-dashboard`,
          { params: queryParams }
        );
        const d = res.data;
        const n = (v) => parseFloat(v) || 0;
        const i = (v) => parseInt(v) || 0;
        commit("SET_DATA_DASHBOARD", {
          tables: {
            top10Winners: d.topWinners || [],
            top10Losers: d.topLosers || [],
          },
          charts: {
            card: {
              turnover: {
                value: n(d.turnover),
                realValue: n(d.realTurnover),
                bonusValue: n(d.bonusTurnover),
                splitResultedPct: n(d.resultedPct),
                splitUnresultedPct: n(d.unresultedPct),
              },
              ggr: { value: n(d.ggr) },
              ngr: { value: n(d.ngr) },
              cashInBank: { value: n(d.cashInBank), deltaTodayPct: 0 },
              ggrSplitBonusVsCash: {
                bonusPct: n(d.bonusGgrPct),
                cashPct: n(d.cashGgrPct),
                cashMarginDeltaPp: 0,
              },
              betSplitRacingVsSport: {
                racingPct: n(d.racingPct),
                sportPct: n(d.sportPct),
              },
              betTypeSplit: {
                singlePct: n(d.singlesPct),
                multiPct: n(d.multiPct),
                exoticPct: n(d.exoticsPct),
              },
              activeClients: { value: i(d.activeClients) },
              newSignupsThisMonth: { value: i(d.newSignups) },
              closedAccounts: { count: i(d.closedAccounts) },
              unsettledBets: { count: i(d.unsettledBets) },
              betstop: { count: i(d.betstop) },
              affiliateNgrPayouts: { value: 0 },
              ytdRevenue: { value: n(d.ytdRevenue), budget: 0, variancePct: 0 },
              ytdTurnover: { value: n(d.ytdTurnover), budget: 0, variancePct: 0 },
              ytdYield: { value: n(d.ytdYield), budget: 0, variancePct: 0 },
              wtdRevenue: { value: n(d.wtdRevenue), budget: 0, variancePct: 0 },
              wtdTurnover: { value: n(d.wtdTurnover), budget: 0, variancePct: 0 },
              wtdYield: { value: n(d.wtdYield), budget: 0, variancePct: 0 },
            },
            racingOverview: {
              settledTotalBets: i(d.racingSettledTotal),
              settledBets: i(d.racingSettledCash),
              settledBonusBets: i(d.racingSettledBonus),
              rejectedBets: i(d.racingRejected),
              unsettledBets: i(d.racingUnsettled),
              unsettledBonusBets: 0,
              unsettledAmount: n(d.racingUnsettledAmount),
              unsettledBonusAmount: 0,
              betsStake: n(d.racingBetsStake),
              bonusResult: n(d.racingBonusResult),
            },
            sportOverview: {
              settledTotalBets: i(d.sportSettledTotal),
              settledBets: i(d.sportSettledCash),
              settledBonusBets: i(d.sportSettledBonus),
              rejectedBets: i(d.sportRejected),
              unsettledBets: i(d.sportUnsettled),
              unsettledBonusBets: 0,
              unsettledAmount: n(d.sportUnsettledAmount),
              unsettledBonusAmount: 0,
              betsStake: n(d.sportBetsStake),
              bonusResult: n(d.sportBonusResult),
            },
          },
        });
      } catch (e) {
        errorHandler(e);
      } finally {
        commit("LOADED");
      }
    },
  },
};
